// Ein Fenster für alle Tarif-Hinweise (Ereignis „nt:tarif“ aus api.ts bei 402 bzw. 503 „bald“, oder aus upgrade.ts,
// wenn die Oberfläche schon vorher weiß, dass der Tarif etwas nicht enthält). Der Text kommt aus details: was der
// nötige Tarif bringt und was er kostet. Der Inhaber bucht direkt (monatlich, Stripe) oder sieht alle Tarife; ein
// Team-Mitglied bekommt nur den Hinweis, sich an den Inhaber zu wenden. „Zugang ruht“ zeigt das Rechnungswerkzeug als
// eigene Seite (ZugangRuht.tsx); hier nur, wenn es mitten in der Arbeit passiert.
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { angebot, euro, naechsterMonat } from './anzeige';
import { bezahlFehler, zurKasse } from './bezahlen';
import { ruhtSeiteSichtbar } from './ruht';
import type { BezahlPlan, Tarif, TarifFehler } from './types';
import { BRINGT, planName } from './upgrade';
import './hinweise.css';

const BEKANNT = ['tarif_feature', 'tarif_grenze', 'bald', 'post_zahlung', 'firmen_grenze'];
// Seiten aus src/billtoro, die ohne Übersicht auskommen und „Zugang ruht“ selbst zeigen
const EIGENE = /^\/rechnung\/(konto|firma-neu|tarif|team|steuerbuero)(\/|$)/;

function bringt(f: Extract<TarifFehler, { code: 'tarif_feature' | 'tarif_grenze' }>, tarif: Tarif, noetig: BezahlPlan) {
  const ziel = angebot(tarif, noetig);
  if (f.code === 'tarif_feature') {
    if (f.details.feature === 'team') return `arbeiten bis zu ${ziel?.grenzen.nutzer ?? 5} Personen mit, jede mit eigenem Login.`;
    return BRINGT[f.details.feature] ?? null;
  }
  const g = ziel?.grenzen[f.details.grenze];
  switch (f.details.grenze) {
    case 'rechnungenMonat':
      return 'schreiben Sie unbegrenzt Rechnungen.';
    case 'angeboteMonat':
      return 'schreiben Sie unbegrenzt Angebote.';
    case 'nutzer':
      return `arbeiten bis zu ${g ?? 'beliebig viele'} Personen mit, jede mit eigenem Login.`;
    case 'abos':
      return g == null ? 'legen Sie unbegrenzt wiederkehrende Rechnungen an.' : `legen Sie bis zu ${g} wiederkehrende Rechnungen an.`;
  }
}

export function UpgradeDialog({ firmId, tarif }: { firmId?: string | null; tarif?: Tarif | null }) {
  const [fehler, setFehler] = useState<TarifFehler | null>(null);
  const [busy, setBusy] = useState(false);
  const [kasseFehler, setKasseFehler] = useState<string | null>(null);
  const ref = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();
  const pfad = useRef(pathname);
  pfad.current = pathname;

  useEffect(() => {
    let warten: ReturnType<typeof setTimeout> | undefined;
    const on = (e: Event) => {
      const d = (e as CustomEvent<TarifFehler>).detail;
      if (!d || !BEKANNT.includes(d.code)) return;
      const zeigen = () => {
        setKasseFehler(null);
        setBusy(false);
        setFehler(d);
      };
      if (d.code === 'tarif_feature' && d.details.ruht) {
        // Die Übersicht selbst ruht: Das Rechnungswerkzeug zeigt gleich die Seite „Ihr Zugang ruht“ (erst nach dem
        // Fehler, darum kurz warten). Die eigenen Seiten zeigen es selbst.
        if (EIGENE.test(pfad.current)) return;
        clearTimeout(warten);
        warten = setTimeout(() => !ruhtSeiteSichtbar() && zeigen(), 300);
        return;
      }
      zeigen();
    };
    window.addEventListener('nt:tarif', on);
    return () => {
      clearTimeout(warten);
      window.removeEventListener('nt:tarif', on);
    };
  }, []);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (fehler && !d.open) d.showModal();
    if (!fehler && d.open) d.close();
  }, [fehler]);

  const zu = () => setFehler(null);

  async function buchen(plan: BezahlPlan) {
    if (!firmId) return;
    setBusy(true);
    setKasseFehler(null);
    try {
      await zurKasse(firmId, plan, 'monat');
    } catch (err) {
      setKasseFehler(bezahlFehler(err));
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="dlg bt-up"
      aria-labelledby="bt-up-titel"
      aria-describedby="bt-up-text"
      onClose={zu}
      onCancel={(e) => {
        e.preventDefault();
        zu();
      }}
      onClick={(e) => {
        if (e.target === ref.current) zu();
      }}
    >
      {fehler && (
        <div className="dlg-in">
          <Inhalt fehler={fehler} tarif={tarif ?? null} firmId={firmId ?? null} busy={busy} kasseFehler={kasseFehler} onBuchen={buchen} onZu={zu} />
        </div>
      )}
    </dialog>
  );
}

function Inhalt({
  fehler,
  tarif,
  firmId,
  busy,
  kasseFehler,
  onBuchen,
  onZu,
}: {
  fehler: TarifFehler;
  tarif: Tarif | null;
  firmId: string | null;
  busy: boolean;
  kasseFehler: string | null;
  onBuchen(plan: BezahlPlan): void;
  onZu(): void;
}) {
  const schliessen = (
    <button type="button" className="btn" onClick={onZu}>
      Schließen
    </button>
  );

  if (fehler.code === 'bald') {
    return (
      <>
        <span className="bt-up-zeichen bald" aria-hidden="true">
          bald
        </span>
        <h2 id="bt-up-titel">Kommt bald</h2>
        <p id="bt-up-text">{fehler.error} Wir sagen Bescheid, sobald es so weit ist.</p>
        <div className="dlg-actions">
          <button type="button" className="btn primary" onClick={onZu} autoFocus>
            Verstanden
          </button>
        </div>
      </>
    );
  }

  if (fehler.code === 'tarif_feature' && fehler.details.ruht) {
    return (
      <>
        <h2 id="bt-up-titel">Ihr Zugang ruht</h2>
        <p id="bt-up-text">{fehler.error}</p>
        <div className="dlg-actions">
          {schliessen}
          <button type="button" className="btn primary" onClick={() => window.location.reload()} autoFocus>
            Seite neu laden
          </button>
        </div>
      </>
    );
  }

  const mitglied = tarif?.rolle === 'mitglied';
  const tarifeLink = (plan?: string | null, primary = false) =>
    mitglied ? null : (
      <Link className={`btn${primary ? ' primary' : ''}`} to={plan ? `/rechnung/tarif?plan=${plan}` : '/rechnung/tarif'} onClick={onZu} autoFocus={primary}>
        {primary ? 'Tarife ansehen' : 'Alle Tarife'}
      </Link>
    );

  // „Weitere Firma anlegen“ über die freien Firmen hinaus (POST /api/billtoro/firmen): die Grenze gilt je Login
  if (fehler.code === 'firmen_grenze') {
    const frei = fehler.details?.grenze;
    return (
      <>
        <h2 id="bt-up-titel">Weitere Firma mit Tarif</h2>
        <p id="bt-up-text" className="bt-up-satz">
          {fehler.error}
        </p>
        <p className="muted bt-up-klein">
          {frei ? `Ohne gebuchten Tarif sind ${frei} Firmen je Login frei. ` : ''}Sobald eine Ihrer Firmen einen Tarif hat,
          legen Sie hier weitere Firmen an.
        </p>
        <div className="dlg-actions">
          {schliessen}
          {tarifeLink(null, true)}
        </div>
      </>
    );
  }

  if (fehler.code === 'post_zahlung') {
    return (
      <>
        <h2 id="bt-up-titel">Erst einen Tarif buchen</h2>
        <p id="bt-up-text">{fehler.error}</p>
        {mitglied && <p className="muted">Bitte wenden Sie sich an den Inhaber der Firma.</p>}
        <div className="dlg-actions">
          {schliessen}
          {tarifeLink(null, true)}
        </div>
      </>
    );
  }

  const noetig = fehler.details.noetig;
  const ziel = tarif && noetig ? angebot(tarif, noetig) : undefined;
  // Ohne Tarif (Übersicht nicht geladen) oder ohne passenden Tarif: nur der Text vom Server
  if (!tarif || !noetig || !ziel) {
    return (
      <>
        <h2 id="bt-up-titel">Mehr mit Billtoro</h2>
        <p id="bt-up-text">{fehler.error}</p>
        <div className="dlg-actions">
          {schliessen}
          {noetig && tarifeLink(noetig, true)}
        </div>
      </>
    );
  }

  const name = planName(tarif, noetig);
  const satz = bringt(fehler, tarif, noetig);
  const monatlich = fehler.code === 'tarif_grenze' && (fehler.details.grenze === 'rechnungenMonat' || fehler.details.grenze === 'angeboteMonat');
  const kannBuchen = !mitglied && tarif.billing && !!firmId;
  return (
    <>
      <span className="bt-up-zeichen" aria-hidden="true">
        {name}
      </span>
      <h2 id="bt-up-titel">Mehr mit {name}</h2>
      <p id="bt-up-text" className="bt-up-satz">
        {satz ? (
          <>
            Mit {name} ({euro(ziel.monat)} / Monat {tarif.hinweisPreis}) {satz}
          </>
        ) : (
          fehler.error
        )}
      </p>
      {satz && (
        <p className="muted bt-up-klein">
          {fehler.error}
          {monatlich && ` Am ${naechsterMonat()} geht es im Tarif ${planName(tarif, tarif.effektiv)} wieder von vorn los.`}
        </p>
      )}
      <ul className="bt-haken bt-up-liste">
        {ziel.merkmale.slice(0, 4).map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
      {mitglied && <p className="notice info">Bitte wenden Sie sich an den Inhaber der Firma.</p>}
      {!mitglied && !tarif.billing && <p className="notice info">Bezahlung folgt in Kürze. Die Tarife können Sie schon ansehen.</p>}
      {kasseFehler && (
        <p className="notice bad" role="alert">
          {kasseFehler}
        </p>
      )}
      <div className="dlg-actions bt-up-akt">
        {schliessen}
        {tarifeLink(noetig, !kannBuchen)}
        {kannBuchen && (
          <button type="button" className="btn primary" disabled={busy} onClick={() => onBuchen(noetig)} autoFocus>
            {busy ? 'Einen Moment …' : `${name} buchen`}
          </button>
        )}
      </div>
      {kannBuchen && <p className="muted bt-up-fuss">Monatlich kündbar. {tarif.hinweisB2B}</p>}
    </>
  );
}
